// pages/admin/SystemSettings.jsx — B3: System Settings & Feature Flags
import { useState, useEffect } from 'react';
import { getSystemConfig, updateSystemConfig, toggleFeatureFlag } from '../api/kailux';
import { invalidateFlagCache } from '../hooks/useFeatureFlag';
import Spinner from '../components/Spinner'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
const inp = {
  width:'100%', padding:'8px 12px', background: T.s1, border:`1px solid ${T.bd}`,
  borderRadius:'8px', color: T.tx, fontSize:'14px', outline:'none',
  fontFamily:"'Outfit',sans-serif", boxSizing:'border-box',
}

const FLAG_META = {
  workpapers_enabled:          { label:'Workpapers Engine',         desc:'Lead schedules, sign-off, locking' },
  bank_statement_enabled:      { label:'Bank Statement Upload',      desc:'SA bank statement → AFS flow' },
  signoff_enabled:             { label:'Director Sign-off Portal',   desc:'Email link → portal → signature' },
  xbrl_export_enabled:         { label:'iXBRL Export',              desc:'CIPC-ready iXBRL file generation (Phase R7)' },
  consolidations_enabled:      { label:'Consolidations',            desc:'Multi-entity group AFS (Phase R8)' },
  benchmark_report_enabled:    { label:'Benchmark Intelligence',    desc:'AI industry comparison report (Phase R9)' },
  intake_portal_enabled:       { label:'Client Intake Portal',      desc:'Secure document collection from clients (Phase E1)' },
  compliance_calendar_enabled: { label:'Compliance Calendar',       desc:'CIPC, IRP6, AFS due dates (Phase E3)' },
  management_accounts_enabled: { label:'Management Accounts',       desc:'Monthly/quarterly reporting (Phase F1)' },
  deferred_tax_enabled:        { label:'Deferred Tax Engine',       desc:'Automated temp difference computation (Phase R3)' },
  additional_documents_enabled:{ label:'Additional Documents',      desc:'Source doc upload + verification badges (Phase D1)' },
  // Aug 2026 (kailux-fixes-log.md §17) — genuinely unbuilt features, not
  // yet-another rollout stage of something already live. Flipping these
  // on before the real workflow exists behind them is a professional-
  // soundness risk (an unqualified Review/Audit opinion, or a disclosure
  // set claiming a standard KaiLux doesn't actually implement), so they
  // default OFF everywhere in the backend regardless of this toggle's
  // own on/off state shown here.
  engagement_type_review_enabled: { label:'Independent Review Engagement', desc:'ISRE 2400 — not yet built, enabling this before the real workflow ships risks an unsubstantiated review opinion' },
  engagement_type_audit_enabled:  { label:'Full Audit Engagement',         desc:'Audit engagements. Needs a verified Registered Auditor, credits and the audit file. Report issuing is a separate switch below and must stay OFF until a Registered Auditor has reviewed the report wording.' },
  audit_report_issuance_enabled:  { label:'Issue Audit Reports',            desc:'Lets a Registered Auditor issue an audit report and lock the file. Keep OFF until the audit file content exists and a Registered Auditor has reviewed the report wording, letters and checklists. Also enforced inside the database.' },
  framework_full_ifrs_enabled:    { label:'Full IFRS Reporting Framework', desc:'Listed companies & large corporates — not yet built' },
  framework_frs102_enabled:       { label:'FRS 102 Reporting Framework',   desc:'UK clients — not yet built' },
  framework_us_gaap_enabled:      { label:'US GAAP Reporting Framework',   desc:'American clients — not yet built' },
  // Aug 2026 (kailux-fixes-log.md §21) — Africa's Talking (SMS) isn't
  // connected in production yet. Off by default: with this flag off, the
  // director sign-off page hides the "SMS backup" phone field entirely
  // and no SMS send is attempted even if a phone number somehow reaches
  // the backend. Flip on once a real SMS provider is wired up — no code
  // change or deploy needed at that point.
  sms_otp_backup_enabled:         { label:'SMS OTP Backup (Sign-off)',     desc:'Africa\u2019s Talking not yet connected — leave off until configured, or director sign-off will advertise an SMS backup that can\u2019t actually deliver' },
};

function Toggle({ checked, onChange, disabled }) {
  return (
    <button role="switch" aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)} disabled={disabled}
      style={{
        position:'relative', display:'inline-flex', alignItems:'center',
        width:'44px', height:'24px', borderRadius:'999px', border:'none', cursor:disabled?'not-allowed':'pointer',
        background: checked ? T.or : T.s3, transition:'background 0.2s', opacity:disabled?0.4:1,
        flexShrink:0,
      }}>
      <span style={{
        position:'absolute', width:'18px', height:'18px', borderRadius:'50%',
        background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.3)',
        left: checked ? '23px' : '3px', transition:'left 0.2s',
      }} />
    </button>
  );
}

export default function SystemSettings() {
  const [config,       setConfig]       = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState({});
  const [toast,        setToast]        = useState(null);
  const [maintenance,  setMaintenance]  = useState(false);
  const [maintMsg,     setMaintMsg]     = useState('');
  const [banner,       setBanner]       = useState('');
  const [supportEmail, setSupportEmail] = useState('');

  function showToast(msg, type='success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  useEffect(() => {
    getSystemConfig()
      .then(data => {
        setConfig(data);
        const sys = data.system_settings || {};
        setMaintenance(sys.maintenance_mode || false);
        setMaintMsg(sys.maintenance_message || '');
        setBanner(sys.announcement_banner || '');
        setSupportEmail(sys.support_email || '');
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  async function handleToggleFlag(flag, enabled) {
    setSaving(s => ({...s,[flag]:true}));
    try {
      await toggleFeatureFlag(flag, enabled);
      invalidateFlagCache();
      setConfig(prev => ({...prev, feature_flags:{...prev.feature_flags,[flag]:enabled}}));
      showToast(`${FLAG_META[flag]?.label||flag} ${enabled?'enabled':'disabled'}`);
    } catch { showToast('Failed to save flag','error'); }
    finally { setSaving(s => ({...s,[flag]:false})); }
  }

  async function saveSystemSettings() {
    setSaving(s => ({...s,system:true}));
    try {
      await updateSystemConfig('system_settings', {
        maintenance_mode:    maintenance,
        maintenance_message: maintMsg,
        announcement_banner: banner,
        support_email:       supportEmail,
        trial_duration_days: config?.system_settings?.trial_duration_days || 30,
        max_file_size_mb:    config?.system_settings?.max_file_size_mb || 10,
      });
      showToast('System settings saved');
    } catch { showToast('Save failed','error'); }
    finally { setSaving(s => ({...s,system:false})); }
  }

  if (loading) return (
    <div style={{height:'200px',display:'flex',alignItems:'center',justifyContent:'center'}}><Spinner/></div>
  );

  const flags = config?.feature_flags || {};

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'24px'}}>
      <div>
        <h2 style={{fontSize:'18px',fontWeight:700,color:T.tx,margin:0}}>System Settings</h2>
        <p style={{fontSize:'14px',fontWeight:'400',color:T.mt,marginTop:'3px'}}>Feature flags and platform-wide controls.</p>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{padding:'10px 14px',borderRadius:'8px',fontSize:'14px',fontWeight:600,
          background:toast.type==='error'?T.rdd:T.gnd,
          color:toast.type==='error'?T.rd:T.gn,
          border:`1px solid ${toast.type==='error'?alpha(T.rd, 0.25):alpha(T.gn, 0.25)}`}}>
          {toast.msg}
        </div>
      )}

      {/* Feature Flags */}
      <section style={{background:T.s2,border:`1px solid ${T.bd}`,borderRadius:'12px',overflow:'hidden'}}>
        <div style={{padding:'14px 18px',borderBottom:`1px solid ${T.bd}`}}>
          <h3 style={{fontSize:'14px',fontWeight:700,color:T.tx,margin:0}}>Feature Flags</h3>
          <p style={{fontSize:'13px',color:T.mt,marginTop:'3px'}}>
            Toggle features ON or OFF instantly — no redeploy. Changes affect all firms immediately.
          </p>
        </div>
        <div>
          {Object.entries(flags).map(([flag, enabled], i, arr) => {
            const meta = FLAG_META[flag] || { label:flag, desc:'' };
            return (
              <div key={flag} style={{
                display:'flex',alignItems:'center',justifyContent:'space-between',
                padding:'13px 18px',
                borderBottom: i < arr.length-1 ? `1px solid ${T.bd}` : 'none',
              }}>
                <div>
                  <p style={{fontSize:'14px',fontWeight:600,color:T.tx,margin:0}}>{meta.label}</p>
                  <p style={{fontSize:'12px',color:T.mt,marginTop:'2px'}}>{meta.desc}</p>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:'8px',flexShrink:0}}>
                  <span style={{fontSize:'12px',fontWeight:700,color:enabled?T.gn:T.mt}}>
                    {enabled?'ON':'OFF'}
                  </span>
                  <Toggle checked={!!enabled} onChange={val=>handleToggleFlag(flag,val)} disabled={!!saving[flag]} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Platform Settings */}
      <section style={{background:T.s2,border:`1px solid ${T.bd}`,borderRadius:'12px',overflow:'hidden'}}>
        <div style={{padding:'14px 18px',borderBottom:`1px solid ${T.bd}`}}>
          <h3 style={{fontSize:'14px',fontWeight:700,color:T.tx,margin:0}}>Platform Settings</h3>
          <p style={{fontSize:'13px',color:T.mt,marginTop:'3px'}}>Maintenance mode, banners, and defaults.</p>
        </div>
        <div style={{padding:'16px',display:'flex',flexDirection:'column',gap:'16px'}}>

          {/* Maintenance */}
          <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'20px'}}>
            <div style={{flex:1}}>
              <label style={{fontSize:'14px',fontWeight:600,color:T.tx,display:'block'}}>Maintenance Mode</label>
              <p style={{fontSize:'12px',color:T.mt,marginTop:'3px'}}>
                Blocks all non-admin access to the platform. Admins bypass automatically.
              </p>
              {maintenance && (
                <input style={{...inp,marginTop:'8px',borderColor:alpha(T.am, 0.38),background:T.amd}}
                  placeholder="Maintenance message shown to users…"
                  value={maintMsg} onChange={e=>setMaintMsg(e.target.value)} />
              )}
            </div>
            <Toggle checked={maintenance} onChange={setMaintenance} />
          </div>

          {/* Banner */}
          <div>
            <label style={{fontSize:'14px',fontWeight:600,color:T.tx,display:'block'}}>Announcement Banner</label>
            <p style={{fontSize:'12px',color:T.mt,marginTop:'3px'}}>Shown to all firms at the top of the app. Leave empty to hide.</p>
            <input style={{...inp,marginTop:'8px'}}
              placeholder="e.g. We're performing maintenance on Saturday at 2am SAST…"
              value={banner} onChange={e=>setBanner(e.target.value)} />
            {banner && (
              <div style={{marginTop:'8px',display:'flex',alignItems:'center',gap:'8px',
                background:T.bld,border:`1px solid ${alpha(T.bl, 0.25)}`,borderRadius:'8px',padding:'10px 14px'}}>
                <span style={{fontSize:'18px'}}><Icon name="megaphone" /></span>
                <span style={{fontSize:'14px',color:T.bl}}>{banner}</span>
              </div>
            )}
          </div>

          {/* Support Email */}
          <div>
            <label style={{fontSize:'14px',fontWeight:600,color:T.tx,display:'block',marginBottom:'6px'}}>Support Email</label>
            <input type="email" style={inp} value={supportEmail} onChange={e=>setSupportEmail(e.target.value)} />
          </div>

          {/* Save */}
          <button onClick={saveSystemSettings} disabled={saving.system}
            style={{alignSelf:'flex-start',padding:'9px 20px',background:T.prg, color:T.onpri,
              border:'none',borderRadius:'8px',fontSize:'14px',fontWeight:700,
              cursor:saving.system?'not-allowed':'pointer',opacity:saving.system?0.6:1,
              fontFamily:"'Outfit',sans-serif"}}>
            {saving.system?'Saving…':'Save Settings'}
          </button>
        </div>
      </section>

      {/* Active Features Summary */}
      <section style={{background:T.s2,border:`1px solid ${T.bd}`,borderRadius:'12px',padding:'16px 18px'}}>
        <h3 style={{fontSize:'14px',fontWeight:700,color:T.tx,margin:'0 0 10px'}}>Active Features Summary</h3>
        <div style={{display:'flex',flexWrap:'wrap',gap:'4px'}}>
          {Object.entries(flags).map(([flag,enabled]) => (
            <span key={flag} style={{padding:'4px 10px',borderRadius:'999px',fontSize:'12px',fontWeight:600,
              background:enabled?T.ord:T.s3,color:enabled?T.or:T.mt,
              textDecoration:enabled?'none':'line-through'}}>
              {FLAG_META[flag]?.label||flag}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
